// ═══ 수기 미수 조정 (말소대행 정산) — 등록 모달 · 진입 버튼 · 장부 하단 조정 목록 ═══
// 서버 계약 (routes/settlementAdjustments.js):
//   POST /settlement-adjustments {unitId, month, amount, reason, date?, agencyId?} → {success, id, entry}
//   GET  /settlement-adjustments?month=&unitId=|accountId=&agencyId= → {success, adjustments:[...], total, importTotal}
//   POST /settlement-adjustments/:id/reverse {reason} → {success, id} · 409 VERSION_CONFLICT = 이미 취소됨
// 귀속월 = 장부에서 보고 있는 달(당월 청구 버킷). 단위(work_unit) 필수 — 계정 레벨 조정은 1차 범위 밖(D3).
// 원장은 불변 — 정정은 삭제가 아니라 [취소](반대 전기)다. 조정이 없는 달은 목록이 아무것도 그리지 않는다.
import React, { useState } from 'react';
import { API, authFetch } from '../utils/api';
import { useIsMobile, hScroll } from '../utils/useMobile';
import { formatArrivedAtKst } from '../utils/dateKst';
import { ls, is } from './SettlementItemDrawer';

var REASON_MAX = 200;
var AMOUNT_MAX = 1000000000; // 서버 상한 |amount| ≤ 1,000,000,000 과 같은 값

var fmt = function (n) { return (parseInt(n, 10) || 0).toLocaleString(); };
// 부호 표기 — LedgerDrawer 와 같은 규칙 (+ 미수 증가 / − 미수 감소)
var signed = function (n) {
  var v = parseInt(n, 10) || 0;
  return (v > 0 ? '+' : (v < 0 ? '−' : '')) + fmt(Math.abs(v));
};
var signColor = function (n) {
  var v = parseInt(n, 10) || 0;
  return v > 0 ? '#dc2626' : (v < 0 ? '#059669' : '#64748b');
};

// 미취소 조정 합 — 요약 스트립 '그 달 청구'·'미수 (그 달 말)' 가산분 (서버 total 과 같은 정의).
export function adjustmentTotal(list) {
  return (list || []).reduce(function (s, a) { return a.reversed ? s : s + (parseInt(a.amount, 10) || 0); }, 0);
}

// 조회 — 단위(u:) 는 unitId, 계정(a:) 은 accountId, 전체는 좌표 없이(대행사 전체).
// → { adjustments(수기 목록), importTotal(엑셀 이관 조정 합 — 목록에 없는 스트립 가산분) }.
// 구 서버(엔드포인트 없음·비JSON)는 빈 목록·0 → 화면 불변. 네트워크 실패는 reject — 호출부가 직전 목록을
// 유지해야 조정분만 빠진 합계가 조용히 보이지 않는다.
export function fetchAdjustments(agencyId, month, unitId, accountId) {
  var none = { adjustments: [], importTotal: 0 };
  if (!agencyId || !month) return Promise.resolve(none);
  var qs = '?month=' + encodeURIComponent(month) + '&agencyId=' + encodeURIComponent(agencyId);
  if (unitId) qs += '&unitId=' + unitId;
  else if (accountId) qs += '&accountId=' + accountId;
  return authFetch(API + '/settlement-adjustments' + qs)
    .then(function (r) {
      return r.json()
        .then(function (d) {
          if (!(d && d.success && Array.isArray(d.adjustments))) return none;
          return { adjustments: d.adjustments, importTotal: parseInt(d.importTotal, 10) || 0 };
        })
        .catch(function () { return none; });
    });
}

// 진입 버튼 — 단위(u:) 스코프만 활성. 계정(a:) 은 흐리게 + 클릭 시 안내, 전체는 비활성.
export function AdjustButton(props) {
  var unitId = props.unitId, accountId = props.accountId;
  var off = !unitId;
  return (<button className="btn btn-outline btn-sm" disabled={!unitId && !accountId}
    title={unitId ? '이 거래처의 이 달 청구를 수기로 조정' : '하위 업체(거래처)를 선택 후 사용'}
    style={off ? Object.assign({ opacity: 0.5, cursor: 'default' }, props.style) : props.style}
    onClick={function () {
      if (unitId) { props.onOpen(); return; }
      if (accountId && props.showToast) props.showToast('대행사 전체가 아닌 하위 업체를 선택하세요', 'err');
    }}>± 수기 조정</button>);
}

var overlay = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 };

// 등록 모달 — props: agencyId · unitId · unitName · month('YYYY-MM') · showToast · onClose · onSaved
export default function SettlementAdjustModal(props) {
  var isMobile = useIsMobile();
  var showToast = props.showToast || function () {};
  var onClose = props.onClose || function () {};
  var [sign, setSign] = useState(1);
  var [amount, setAmount] = useState(''); // 숫자만 저장, 표시는 천 단위 구분
  var [reason, setReason] = useState('');
  var [date, setDate] = useState('');
  var [busy, setBusy] = useState(false);

  var amt = parseInt(amount, 10) || 0;
  var reasonLen = reason.trim().length;
  var canSubmit = !busy && amt > 0 && amt <= AMOUNT_MAX && reasonLen > 0 && reasonLen <= REASON_MAX;
  var mm = /^(\d{4})-(\d{2})$/.exec(props.month || '');
  var monthText = mm ? (mm[1] + '년 ' + parseInt(mm[2], 10) + '월') : (props.month || '-');

  var submit = function () {
    if (!canSubmit) return;
    setBusy(true);
    var body = { agencyId: parseInt(props.agencyId, 10) || 0, unitId: props.unitId, month: props.month,
      amount: sign * amt, reason: reason.trim() };
    if (date) body.date = date;
    authFetch(API + '/settlement-adjustments', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        setBusy(false);
        if (!d || !d.success) { showToast((d && d.error) || '등록 실패', 'err'); return; }
        showToast('수기 조정 등록 완료');
        if (props.onSaved) props.onSaved(d);
      }).catch(function () { setBusy(false); showToast('서버 오류', 'err'); });
  };

  var signBtn = function (v, label) {
    var on = sign === v, c = v > 0 ? '#dc2626' : '#059669';
    return (<button key={v} type="button" aria-pressed={on} onClick={function () { setSign(v); }}
      style={{ flex: 1, padding: '8px 10px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
        border: '1px solid ' + (on ? c : '#d0d5dd'), background: on ? (v > 0 ? '#fef2f2' : '#ecfdf5') : '#fff', color: on ? c : '#64748b' }}>{label}</button>);
  };

  return (<div style={overlay} onClick={function () { if (!busy) onClose(); }}>
    <div style={isMobile
      ? { background: '#fff', borderRadius: 12, padding: 16, width: '100%', maxWidth: '94vw', maxHeight: '88vh', overflowY: 'auto' }
      : { background: '#fff', borderRadius: 12, padding: 24, width: 440, maxWidth: '92vw', maxHeight: '90vh', overflowY: 'auto' }}
      onClick={function (e) { e.stopPropagation(); }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>수기 미수 조정</h3>
        <button onClick={function () { if (!busy) onClose(); }} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#94a3b8' }}>✕</button>
      </div>
      <p style={{ margin: '0 0 6px', fontSize: 12, color: '#64748b' }}>{props.unitName || ''}{props.unitName ? ' · ' : ''}귀속월 {monthText}</p>

      <label style={ls}>구분</label>
      <div style={{ display: 'flex', gap: 6 }}>
        {signBtn(1, '+ 가산 (미수 증가)')}
        {signBtn(-1, '− 차감 (미수 감소)')}
      </div>
      <label style={ls}>금액</label>
      <input inputMode="numeric" style={Object.assign({}, is, { textAlign: 'right' })} placeholder="0"
        value={amount ? fmt(amount) : ''}
        onChange={function (e) { setAmount(String(e.target.value || '').replace(/[^\d]/g, '').slice(0, 10)); }}/>
      {amt > AMOUNT_MAX && <div style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>{fmt(AMOUNT_MAX)}원 이하로 입력하세요</div>}
      <label style={ls}>사유 (필수)</label>
      <input style={is} value={reason} placeholder="예: 3월 등록세 누락분" onChange={function (e) { setReason(e.target.value); }}/>
      <div style={{ fontSize: 11, marginTop: 4, textAlign: 'right', color: reasonLen > REASON_MAX ? '#dc2626' : '#94a3b8' }}>{reasonLen}/{REASON_MAX}</div>
      <label style={ls}>날짜 (선택)</label>
      <input type="date" style={is} value={date} onChange={function (e) { setDate(e.target.value); }}/>

      <div style={{ marginTop: 14, padding: '10px 14px', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
        <span style={{ color: '#64748b' }}>{monthText} 청구에 반영</span>
        <span style={{ fontWeight: 700, color: signColor(sign * amt) }}>{signed(sign * amt)}원</span>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button className="btn btn-outline" style={{ flex: 1, justifyContent: 'center' }} disabled={busy} onClick={onClose}>취소</button>
        <button className="btn btn-blue" style={{ flex: 1, justifyContent: 'center', opacity: canSubmit ? 1 : 0.5, cursor: canSubmit ? 'pointer' : 'default' }}
          disabled={!canSubmit} onClick={submit}>{busy ? '등록 중…' : '등록'}</button>
      </div>
    </div>
  </div>);
}

// 장부 표 아래 조정 목록 — props: adjustments · showUnit(거래처 열, 단위 스코프가 아닐 때) · showToast · onChanged(재조회)
export function SettlementAdjustList(props) {
  var list = props.adjustments || [];
  var showToast = props.showToast || function () {};
  var onChanged = props.onChanged || function () {};
  var isMobile = useIsMobile();
  var [busyId, setBusyId] = useState(0);
  if (!list.length) return null;

  var reverse = function (a) {
    if (busyId) return;
    var reason = window.prompt(signed(a.amount) + '원 조정을 취소합니다. 취소 사유를 입력하세요');
    if (reason === null) return;
    reason = String(reason).trim();
    if (!reason) { showToast('취소 사유를 입력하세요', 'err'); return; }
    setBusyId(a.id);
    authFetch(API + '/settlement-adjustments/' + a.id + '/reverse', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason: reason }) })
      .then(function (r) { return r.json().then(function (d) { return { status: r.status, data: d }; }); })
      .then(function (res) {
        setBusyId(0);
        var d = res.data || {};
        if (res.status === 409 && d.code === 'VERSION_CONFLICT') {
          showToast((d.error || '이미 취소된 조정입니다') + ' — 최신 내용으로 다시 불러왔습니다', 'err');
          onChanged();
          return;
        }
        if (!d.success) { showToast(d.error || '취소 실패', 'err'); return; }
        showToast('조정 취소 완료');
        onChanged();
      }).catch(function () { setBusyId(0); showToast('서버 오류', 'err'); });
  };

  var th = { padding: '6px 10px', fontSize: 11, fontWeight: 600, textAlign: 'left', whiteSpace: 'nowrap' };
  var td = { padding: '6px 10px', verticalAlign: 'top' };
  var heads = ['날짜'].concat(props.showUnit ? ['거래처'] : []).concat(['금액', '사유', '작성자', '시각', '']);
  var total = adjustmentTotal(list);
  return (<div data-testid="adj-list" style={{ marginTop: 12, padding: 14, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8 }}>
    <div style={{ fontSize: 13, fontWeight: 800, color: '#1B3A5C', marginBottom: 8 }}>수기 조정 {list.length}건</div>
    <div style={isMobile ? hScroll : undefined}>
      <table style={{ width: '100%', minWidth: isMobile ? 560 : undefined, borderCollapse: 'collapse', fontSize: 12 }}>
        <thead><tr style={{ background: 'var(--tbl-hd-bg)', color: '#64748b' }}>
          {heads.map(function (h, i) { return <th key={h + i} style={h === '금액' ? Object.assign({}, th, { textAlign: 'right' }) : th}>{h}</th>; })}
        </tr></thead>
        <tbody>
          {list.map(function (a) {
            var strike = a.reversed ? { textDecoration: 'line-through', opacity: 0.5 } : null;
            return (<tr key={a.id} style={{ borderTop: '1px solid #f1f5f9' }}>
              <td style={Object.assign({ whiteSpace: 'nowrap' }, td, strike)}>{a.date || '-'}</td>
              {!!props.showUnit && <td style={Object.assign({ whiteSpace: 'nowrap', color: '#475569' }, td, strike)}>{a.unitName || '-'}</td>}
              <td style={Object.assign({ textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap', color: signColor(a.amount) }, td, strike)}>{signed(a.amount)}</td>
              <td style={td}>
                <div style={strike || undefined}>{a.reason || ''}</div>
                {!!a.reversed && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                  취소 사유: {a.reverseReason || '-'}{a.reversedByName ? ' · ' + a.reversedByName : ''}{a.reversedAt ? ' · ' + formatArrivedAtKst(a.reversedAt) : ''}
                </div>}
              </td>
              <td style={Object.assign({ whiteSpace: 'nowrap', color: '#475569' }, td)}>{a.createdByName || '-'}</td>
              <td style={Object.assign({ whiteSpace: 'nowrap', color: '#64748b' }, td)}>{formatArrivedAtKst(a.createdAt) || '-'}</td>
              <td style={Object.assign({ whiteSpace: 'nowrap', textAlign: 'right' }, td)}>
                {a.reversed
                  ? <span style={{ fontSize: 11, color: '#94a3b8' }}>취소됨</span>
                  : <button className="btn btn-outline btn-sm" disabled={!!busyId} onClick={function () { reverse(a); }}>취소</button>}
              </td>
            </tr>);
          })}
          <tr style={{ borderTop: '1px solid #e5e7eb', background: '#f8fafc', fontWeight: 700 }}>
            <td colSpan={props.showUnit ? 2 : 1} style={Object.assign({ color: '#334155' }, td)}>조정 합계</td>
            <td style={Object.assign({ textAlign: 'right', whiteSpace: 'nowrap', color: signColor(total) }, td)}>{signed(total)}원</td>
            <td colSpan={4} style={Object.assign({ fontSize: 11, fontWeight: 400, color: '#94a3b8' }, td)}>취소분 제외 · 이 달 청구에 합산</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>);
}
